import { Calendar, Zap } from "lucide-react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getCalendarUrlWithFragment } from "@/domains/appointment/calendar/utils";
import { useAppointmentsUIStore } from "../../../store/use-appointments-ui-store";

function isToday(date: DateTime): boolean {
  const salonToday = DateTime.now();
  return salonToday.hasSame(date, "day");
}

interface MobileDaySectionProps {
  date: DateTime;
  appointmentCount: number;
  hideAutoOrganize?: boolean;
  hideCalendar?: boolean;
}

export function MobileDaySection({
  date,
  appointmentCount,
  hideAutoOrganize = false,
  hideCalendar = false,
}: MobileDaySectionProps) {
  const { t, i18n } = useTranslation("appointments");
  const navigate = useNavigate();
  const { handleAutoOrganize } = useAppointmentsUIStore();

  const dateText = date.setLocale(i18n.language).toFormat("EEE, dd-MM-yyyy");

  const handleNavigateToCalendar = () => {
    const calendarUrl = getCalendarUrlWithFragment("worker", date);
    navigate(calendarUrl);
  };

  const handleAutoOrganizeClick = () => {
    const targetDate = date.toJSDate();
    handleAutoOrganize(targetDate);
  };

  return (
    <div className="sticky top-2 z-20 pb-2">
      <div className="border-b border-border/50 px-3 py-2 shadow-sm overflow-hidden rounded-lg bg-background">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-foreground">{dateText}</h3>
          </div>

          <div className="flex items-center gap-1">
            {!hideCalendar && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToCalendar}
                className="p-1.5 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                title={t("navigation.viewInCalendar")}
              >
                <Calendar className="w-4 h-4" />
              </Button>
            )}

            {appointmentCount > 0 && !hideAutoOrganize && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAutoOrganizeClick}
                className={`p-1.5 transition-colors ${
                  isToday(date)
                    ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    : "hover:bg-indigo-50 hover:text-indigo-700"
                }`}
                title={
                  isToday(date)
                    ? t("organization.autoOrganizeToday")
                    : t("organization.autoOrganize")
                }
              >
                <Zap className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
